import { useMutation, useQueryClient } from '@tanstack/react-query';
import { sendOptInEmails } from '@/lib/api/email-flows';
import { stanNotify } from '@/lib';
import { QUERY_KEYS } from '@/lib/constants';

interface SendOptInEmailPayload {
    subject_template: string;
    body_template: string;
}

interface SendOptInEmailResponse {
    emails_triggered: number;
}

/**
 * Mutation hook for sending opt-in emails to recipients
 */
export const useSendOptInEmailMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload: SendOptInEmailPayload) => sendOptInEmails(payload),
        onSuccess: (data: SendOptInEmailResponse) => {
            // Only show success toast if emails were actually sent
            if (data.emails_triggered > 0) {
                stanNotify({
                    type: 'success',
                    title: 'Opt-in emails sent!',
                    text: `Successfully sent to ${data.emails_triggered} ${data.emails_triggered === 1 ? 'contact' : 'contacts'}`,
                });

                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.RECIPIENTS_LIST],
                });
            }
        },
        onError: (error: unknown) => {
            const errorMessage =
                error && typeof error === 'object' && 'response' in error
                    ? (error.response as { data?: { message?: string } })?.data?.message
                    : undefined;

            stanNotify({
                type: 'error',
                title: 'Something went wrong',
                text: errorMessage || 'We could not process this request.',
            });
        },
    });
};
